import React, { useMemo } from 'react';
import { __ } from '@wordpress/i18n';
import {
	formatFullDate,
	formatDuration,
	formatCalendarDate,
} from '../utils/date-time';
import { useTimeSlotsQuery } from '../queries';
import { useBookingStore } from '../store/useBookingStore';
import {
	useService,
	useEffectiveDuration,
	useSelectedDate,
	useOriginalTime,
} from '../hooks/useBookingComputed';
import { TimeSlots } from './TimeSlots';
import { LoadingSkeleton } from './LoadingSkeleton';
import { DurationPicker } from './DurationPicker';
import './TimeSlotPicker.css';

interface Props {
	onBack: () => void;
	isEntryEdit?: boolean;
}

export function TimeSlotPicker({ onBack, isEntryEdit = false }: Props) {
	const { data: service } = useService();
	const selectedDate = useSelectedDate();
	const originalTime = useOriginalTime();
	const originalRange = useBookingStore((state) => state.originalRange);
	const currentServiceId = useBookingStore((state) => state.currentServiceId);
	const slotMode = useBookingStore((state) => state.slotMode);
	const interval = useBookingStore((state) => state.interval);
	const selectedResourceIds = useBookingStore(
		(state) => state.selectedResourceIds
	);
	const resourceMode = useBookingStore((state) => state.resourceMode);
	const excludeBookingId = useBookingStore((state) => state.excludeBookingId);
	const excludeBookingToken = useBookingStore(
		(state) => state.excludeBookingToken
	);
	const showSpotsLeft = useBookingStore((state) => state.showSpotsLeft);
	const spotsLeftText = useBookingStore((state) => state.spotsLeftText);

	const effectiveDuration = useEffectiveDuration();

	// Fetch time slots
	const {
		data: timeSlotsData,
		isLoading,
		error,
	} = useTimeSlotsQuery(
		{
			serviceId: currentServiceId,
			date: selectedDate ? formatCalendarDate(selectedDate) : '',
			slotMode,
			interval,
			duration: service?.flexibleDuration ? effectiveDuration : 0,
			resources: selectedResourceIds,
			resourceMode,
			excludeBookingId: excludeBookingId || undefined,
			excludeBookingToken: excludeBookingToken || undefined,
		},
		{
			enabled: !!selectedDate && !!service,
		}
	);

	// In entry edit mode, inject a placeholder slot for the original booking time
	// only when viewing the original date (so user can see/select the original time)
	const slotsWithPlaceholder = useMemo(() => {
		const slots = timeSlotsData?.slots ?? [];

		// Only inject placeholder in entry edit mode when we have original time and date
		if (
			!isEntryEdit ||
			!originalTime ||
			!originalRange?.from ||
			!selectedDate
		) {
			return slots;
		}

		// Only show placeholder on the original booking date
		const isOnOriginalDate =
			formatCalendarDate(selectedDate) ===
			formatCalendarDate(originalRange.from);
		if (!isOnOriginalDate) {
			return slots;
		}

		// Check if original time already exists in slots
		const timeExists = slots.some((slot) => slot.time === originalTime);
		if (timeExists) {
			return slots;
		}

		// Create placeholder slot for the original time
		const placeholderSlot = {
			time: originalTime,
			available: false,
			remainingSlots: 0,
		};

		// Insert placeholder in sorted order
		const newSlots = [...slots, placeholderSlot];
		newSlots.sort((a, b) => a.time.localeCompare(b.time));

		return newSlots;
	}, [
		timeSlotsData?.slots,
		isEntryEdit,
		originalTime,
		originalRange,
		selectedDate,
	]);

	if (!selectedDate || !service) {
		return null;
	}

	return (
		<div className="gpb-booking-time-picker__slots-container">
			<div className="gpb-booking-time-picker__header">
				<div>
					<button
						type="button"
						className="gpb-booking-time-picker__back"
						onClick={onBack}
					>
						← {__('Back to Calendar', 'gp-bookings')}
					</button>
				</div>

				<div>
					<h4>{formatFullDate(selectedDate)}</h4>
				</div>
			</div>

			<div className="gpb-booking-time-picker__slots">
				<DurationPicker />
				<h4>
					{isLoading && __('Loading Available Times…', 'gp-bookings')}
					{!isLoading && (
						<>
							{__('Available Times', 'gp-bookings')}
							<small className="gpb-booking-time-picker__duration">
								{
									/* translators: %s: duration in minutes/hours */
									__('(%s)', 'gp-bookings').replace(
										'%s',
										formatDuration(
											timeSlotsData?.blockSize ??
												effectiveDuration
										)
									)
								}
							</small>
						</>
					)}
				</h4>

				{isLoading ? (
					<LoadingSkeleton />
				) : error ? (
					<div className="gpb-booking-time-picker__error">
						{error.message}
					</div>
				) : (
					<TimeSlots
						slots={slotsWithPlaceholder}
						blockSize={
							timeSlotsData?.blockSize ?? service.blockSize
						}
						showSpotsLeft={showSpotsLeft}
						spotsLeftText={spotsLeftText}
						isEntryEdit={isEntryEdit}
					/>
				)}
			</div>
		</div>
	);
}
